// ═══════════════════════════════════════════════════════════════════════
// Paramètres › Dispositifs sociaux — régler un dispositif à l'écran (plus besoin de passer par l'API).
//   1. Portée : organisation → dispositif → (option) une campagne ou un type d'aide ;
//   2. Règles applicables à une date (gabarit + règles communes) : valeur, version, période, origine ; règles
//      obligatoires manquantes (règlement incomplet) ;
//   3. Fiche d'une règle : versions et circuit hors règlement (nouvelle version datée, sourcée, motivée) ;
//   4. Simulation budgétaire du règlement en vigueur (hypothèses) ;
//   5. Règlements (packs de portée) : création, composition, simulation, soumission, validation par une autre personne ;
//   6. Pack de DÉMONSTRATION : dispositif marqué démonstration, base de test ou de recette seulement, marqué [DÉMO].
// L'état de l'écran vit dans l'adresse (?organisation=&dispositif=&portee=&objet=&reglement=&regle=) : un lien depuis
// les écrans des dispositifs ou depuis « À valider » ouvre directement la bonne portée.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, Calculator, ListChecks, Plus, Eye, Inbox } from 'lucide-react';
import { Carte, Bouton, BandeauInfo, Badge, Squelette, formaterDate } from '../../design/composants';
import { useChargement, Champ } from '../temps/reglagesCommun';
import { useAuth } from '../../context/AuthContext';
import { lireCfg, BadgeStatutRegle } from './configurationCommun';
import {
  apiDs, toArray, aujourdHui, libellePortee, LIBELLES_GABARIT, SOURCES_DISPOSITIF, ErreurCfg, erreurCfg,
  FormulaireHypotheses, ResultatBudget, hypothesesDe,
} from './dispositifsCommun';
import { FenetreRegleDispositif } from './VersionRegleDispositif';
import { FicheReglement, FenetreNouveauReglement, BoutonPackDemo, estDemo } from './ReglementPackDispositif';

const LIBELLE_OBJET = { CAMPAGNE: 'Campagne', TYPE_AIDE: 'Type d\'aide' };
const nomObjet = (o) => (o ? o.libelle || o.numero || o.code : '');

/** Choix de la portée : organisation, dispositif, campagne ou type d'aide. */
function SelecteurPortee({ organisations, orgId, dispositifs, dispId, objets, valeurObjet, date, onChanger, onDate }) {
  const dispositif = dispositifs.find((d) => d.id === dispId);
  return (
    <Carte titre="Portée" Icone={Building2}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', columnGap: 12 }}>
        <Champ libelle="Organisation">
          <select id="choix-organisation" className="rh-champ-saisie" value={orgId} onChange={(e) => onChanger({ organisation: e.target.value, dispositif: '', portee: '', objet: '', reglement: '', regle: '' })}>
            <option value="">Choisir…</option>
            {organisations.map((o) => <option key={o.id} value={o.id}>{o.libelle}{o.code ? ` (${o.code})` : ''}</option>)}
          </select>
        </Champ>
        <Champ libelle="Dispositif">
          <select id="choix-dispositif" className="rh-champ-saisie" value={dispId} disabled={!orgId} onChange={(e) => onChanger({ dispositif: e.target.value, portee: '', objet: '', reglement: '', regle: '' })}>
            <option value="">Choisir…</option>
            {dispositifs.map((d) => <option key={d.id} value={d.id}>{d.libelle}{d.donnees_demo && !/\[DÉMO\]/.test(d.libelle) ? ' [DÉMO]' : ''}</option>)}
          </select>
        </Champ>
        {dispositif && (dispositif.type === 'CAMPAGNE' || dispositif.type === 'AIDE') && (
          <Champ libelle={dispositif.type === 'CAMPAGNE' ? 'Campagne (facultatif)' : 'Type d\'aide (facultatif)'}
            aide={dispositif.type === 'CAMPAGNE' ? 'Sans campagne : valeur commune à toutes les campagnes du dispositif.' : 'Sans type : règles du dispositif d\'aides entier.'}>
            <select id="choix-objet" className="rh-champ-saisie" value={valeurObjet} onChange={(e) => {
              const [t, id] = e.target.value ? e.target.value.split(':') : ['', ''];
              onChanger({ portee: t, objet: id, reglement: '', regle: '' });
            }}>
              <option value="">{dispositif.type === 'CAMPAGNE' ? 'Toutes les campagnes (dispositif entier)' : 'Dispositif entier'}</option>
              {objets.map((o) => <option key={o.id} value={`${dispositif.type === 'CAMPAGNE' ? 'CAMPAGNE' : 'TYPE_AIDE'}:${o.id}`}>{nomObjet(o)}{o.numero && o.libelle ? ` — ${o.numero}` : ''}</option>)}
            </select>
          </Champ>
        )}
        {dispositif && (
          <Champ libelle="Règles applicables au">
            <input type="date" id="date-reference" className="rh-champ-saisie" value={date} onChange={(e) => onDate(e.target.value || aujourdHui())} />
          </Champ>
        )}
      </div>
      {dispositif && (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--sg-texte-2)' }}>
          Gabarit : {LIBELLES_GABARIT[dispositif.gabarit] || dispositif.gabarit} · état du dispositif : {dispositif.etat}
          {dispositif.donnees_demo && <> · <Badge ton="alerte">[DÉMO]</Badge></>}
        </p>
      )}
    </Carte>
  );
}

/** Statut d'une règle du règlement lu (réglée, à régler, par campagne, défaut décidé, ambiguë, facultative). */
function StatutRegle({ r, applique, absente, defaut, objetType }) {
  if (applique) return <Badge ton="succes">Réglée</Badge>;
  if (absente?.raison === 'AMBIGUITE') return <Badge ton="danger" title="Deux versions de même précision et même priorité : aucune ne s'applique">Ambiguë</Badge>;
  if (r.obligatoire && (r.portee === 'DISPOSITIF' || objetType)) return <Badge ton="danger">À régler</Badge>;
  if (r.obligatoire) return <Badge ton="alerte" title="À régler pour chaque campagne (ou type), ou en valeur commune">Par {r.portee === 'CAMPAGNE' ? 'campagne' : 'type d\'aide'}</Badge>;
  if (defaut) return <Badge ton="info" title={defaut.libelle}>Défaut décidé</Badge>;
  return <Badge ton="neutre">Facultative</Badge>;
}

/** Règles applicables à la date pour la portée : valeur, version, période, origine, statut. */
function CarteReglesApplicables({ reglement, erreur, regles, communes, objetType, date, onOuvrir }) {
  const lues = reglement?.regles || {};
  const absentes = Object.fromEntries(toArray(reglement?.absentes).map((a) => [a.code, a]));
  const defauts = reglement?.defauts || {};
  const manquantes = regles.filter((r) => r.obligatoire && !lues[r.code] && (r.portee === 'DISPOSITIF' || objetType));
  const parObjet = regles.filter((r) => r.obligatoire && !lues[r.code] && r.portee !== 'DISPOSITIF' && !objetType);
  const codesCommuns = new Set(communes.map((r) => r.code));
  return (
    <Carte titre={`Règles applicables au ${formaterDate(date)}`} Icone={ListChecks}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!reglement && !erreur && <Squelette hauteur={120} />}
      {reglement && (manquantes.length > 0
        ? (
          <BandeauInfo ton="alerte" titre={`Règlement incomplet : ${manquantes.length} règle(s) obligatoire(s) sans valeur validée`}>
            {manquantes.map((r) => r.libelle).join(' ; ')}. Tant qu'elles manquent, {objetType ? 'cette portée' : 'le dispositif'} ne peut pas être activé(e) : aucune valeur n'est inventée.
          </BandeauInfo>
        )
        : <BandeauInfo ton="succes" titre="Règlement complet">Toutes les règles obligatoires de cette portée ont une valeur validée à cette date.</BandeauInfo>)}
      {reglement && parObjet.length > 0 && (
        <p style={{ margin: '0 0 8px', fontSize: 13 }}>{parObjet.length} règle(s) obligatoire(s) se règlent par campagne (ou par type d'aide), ou ici en valeur commune : choisissez une campagne ou un type pour vérifier sa complétude.</p>
      )}
      {reglement && (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau" data-regles-applicables="">
            <thead><tr><th>Règle</th><th>Valeur en vigueur</th><th>Version et période</th><th>Origine</th><th>Statut</th><th aria-label="Action" /></tr></thead>
            <tbody>
              {regles.map((r) => {
                const x = lues[r.code];
                return (
                  <tr key={r.code} data-code={r.code}>
                    <td>
                      {r.libelle}{r.obligatoire && <span className="rh-form-requis" aria-label="obligatoire">*</span>}
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>
                        {codesCommuns.has(r.code) ? 'Commune à tout dispositif' : r.portee === 'DISPOSITIF' ? 'Se règle pour le dispositif entier' : `Se règle par ${r.portee === 'CAMPAGNE' ? 'campagne' : 'type d\'aide'} ou en valeur commune`}
                      </span>
                    </td>
                    <td style={{ maxWidth: 320 }}>{x ? x.lisible ?? String(x.valeur) : defauts[r.code] ? <span style={{ color: 'var(--sg-texte-2)' }}>{defauts[r.code].libelle}</span> : '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{x ? <>V{x.version ?? '?'} · {formaterDate(x.date_debut)} → {x.date_fin ? formaterDate(x.date_fin) : '…'}</> : '—'}</td>
                    <td>{x ? (x.portee === 'DISPOSITIF' ? 'Dispositif entier' : LIBELLE_OBJET[x.portee] || x.portee) : '—'}</td>
                    <td><StatutRegle r={r} applique={x} absente={absentes[r.code]} defaut={defauts[r.code]} objetType={objetType} /></td>
                    <td><Bouton petit Icone={Eye} onClick={() => onOuvrir(r.code)}>Versions</Bouton></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--sg-texte-2)' }}>
        Les règles d'entreprise des retenues (quotité, part maximale, priorités, transmission) ne dépendent pas d'un dispositif : <Link to="/parametres/retenues">Paramètres › Retenues sur salaire</Link>.
      </p>
    </Carte>
  );
}

/** Simulation budgétaire du règlement EN VIGUEUR (non conservée). */
function CarteSimulationEnVigueur({ dispositif, portee, date }) {
  const [hypotheses, setHypotheses] = useState({});
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  if (!['CAMPAGNE', 'PRET', 'AIDE'].includes(dispositif.type)) return null;
  const lancer = async () => {
    setEnCours(true); setErreur(null);
    try {
      setResultat(await apiDs.simulerEnVigueur(dispositif.id, { date, objetType: portee.objetType, objetId: portee.objetId, hypotheses: hypothesesDe(dispositif.type, hypotheses) }));
    } catch (e) { setErreur(erreurCfg(e)); setResultat(null); }
    setEnCours(false);
  };
  return (
    <Carte titre="Simulation budgétaire du règlement en vigueur" Icone={Calculator}>
      <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Calcul sur les règles validées à la date choisie ; rien n'est enregistré ni modifié. Pour un règlement en préparation, utilisez « Simuler le règlement » dans sa fiche.</p>
      <FormulaireHypotheses type={dispositif.type} valeur={hypotheses} onChange={setHypotheses} />
      <Bouton Icone={Calculator} disabled={enCours} onClick={lancer}>Simuler</Bouton>
      <div style={{ marginTop: 12 }}><ErreurCfg erreur={erreur} /><ResultatBudget resultat={resultat} /></div>
    </Carte>
  );
}

/** Règlements (packs) du dispositif ; fiche du règlement ouvert. */
function CarteReglements({ packs, erreur, dispositif, portee, libellePack, onOuvrir, onNouveau, onDemo, erreurDemo }) {
  const liste = toArray(packs);
  return (
    <Carte titre="Règlements du dispositif" Icone={ListChecks} action={<Bouton petit variante="primaire" Icone={Plus} onClick={onNouveau}>Nouveau règlement</Bouton>}>
      <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Un règlement regroupe les règles décidées ensemble pour une portée (dispositif entier, une campagne, un type d'aide), à une même date d'effet ; il est simulé, soumis, puis validé en bloc par une autre personne.</p>
      {dispositif.donnees_demo && dispositif.gabarit === 'CAMPAGNE_VENTE_SUBVENTIONNEE' && (
        <BandeauInfo ton="alerte" titre="[DÉMO] Dispositif de démonstration">
          Le règlement de démonstration contient des valeurs d'exemple, sans valeur pour SOGAS. Il n'est accepté qu'en base de test ou de recette, créé en brouillon, et doit être simulé, soumis et validé par deux personnes.
          <div style={{ marginTop: 8 }}><BoutonPackDemo dispositif={dispositif} portee={portee} onTermine={onDemo} onErreur={erreurDemo.set} /></div>
        </BandeauInfo>
      )}
      <ErreurCfg erreur={erreurDemo.valeur} />
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!packs && !erreur && <Squelette hauteur={60} />}
      {packs && liste.length === 0 && <p style={{ fontSize: 14 }}>Aucun règlement pour ce dispositif.</p>}
      {liste.length > 0 && (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau" data-reglements="">
            <thead><tr><th>Règlement</th><th>Portée</th><th>Période</th><th>Source</th><th>Statut</th><th aria-label="Action" /></tr></thead>
            <tbody>
              {liste.map((p) => (
                <tr key={p.id} data-pack={p.id}>
                  <td>{p.libelle} <span className="rh-cellule-secondaire">V{p.version}</span>{estDemo(p) && <> <Badge ton="alerte">[DÉMO]</Badge></>}</td>
                  <td>{libellePack(p)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formaterDate(p.date_debut)} → {p.date_fin ? formaterDate(p.date_fin) : '…'}</td>
                  <td>{SOURCES_DISPOSITIF[p.source_nature] || p.source_nature}</td>
                  <td><BadgeStatutRegle statut={p.statut} /></td>
                  <td><Bouton petit Icone={Eye} onClick={() => onOuvrir(p.id)}>Ouvrir</Bouton></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Carte>
  );
}

/** Règlements et versions du dispositif qui attendent une validation. */
function CarteAValider({ donnees, dispId, onOuvrirPack, onOuvrirRegle, libellePack }) {
  const packs = toArray(donnees?.packs).filter((p) => p.portee_dispositif_id === dispId);
  const versions = toArray(donnees?.versions).filter((v) => v.portee_dispositif_id === dispId);
  if (!packs.length && !versions.length) return null;
  return (
    <Carte titre={`À valider pour ce dispositif (${packs.length + versions.length})`} Icone={Inbox}>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
        {packs.map((p) => (
          <li key={p.id}>Règlement « {p.libelle} » ({libellePack(p)}) — préparé par {p.auteur || '—'} · <button type="button" className="rh-lien" onClick={() => onOuvrirPack(p.id)}>ouvrir</button></li>
        ))}
        {versions.map((v) => (
          <li key={v.id}>{v.libelle} : {v.valeur} à partir du {formaterDate(v.date_debut)} — préparée par {v.auteur || '—'} · <button type="button" className="rh-lien" onClick={() => onOuvrirRegle(v)}>ouvrir</button></li>
        ))}
      </ul>
    </Carte>
  );
}

export default function ReglementDispositifPanneau() {
  const { employe } = useAuth();
  const moi = employe?.id || null;
  const [params, setParams] = useSearchParams();
  const orgId = params.get('organisation') || '';
  const dispId = params.get('dispositif') || '';
  const objetType = ['CAMPAGNE', 'TYPE_AIDE'].includes(params.get('portee')) ? params.get('portee') : '';
  const objetId = objetType ? params.get('objet') || '' : '';
  const packOuvert = params.get('reglement') || '';
  const regleOuverte = params.get('regle') || '';
  const [date, setDate] = useState(aujourdHui());
  const [fenetre, setFenetre] = useState(null);
  const [erreurDemo, setErreurDemo] = useState(null);
  const changer = (maj) => {
    const p = new URLSearchParams(params);
    Object.entries(maj).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    setParams(p, { replace: true });
  };

  const orgs = useChargement(() => apiDs.organisations(), []);
  const gab = useChargement(() => apiDs.gabarits(), []);
  const disps = useChargement(() => (orgId ? apiDs.dispositifs(orgId) : Promise.resolve(null)), [orgId]);
  const dispositif = toArray(disps.donnees).find((d) => d.id === dispId) || null;
  const typeDisp = dispositif ? dispositif.type : null;
  const objets = useChargement(() => {
    if (!dispId || !orgId || !typeDisp) return Promise.resolve(null);
    if (typeDisp === 'CAMPAGNE') return apiDs.campagnes(orgId).then((x) => toArray(x).filter((c) => c.dispositif_id === dispId));
    if (typeDisp === 'AIDE') return apiDs.typesAide(dispId).then(toArray);
    return Promise.resolve([]);
  }, [dispId, orgId, typeDisp]);
  const reg = useChargement(() => (dispositif ? apiDs.reglement(dispId, { date, objetType, objetId }) : Promise.resolve(null)), [dispositif?.id, date, objetType, objetId]);
  const packs = useChargement(() => (dispositif ? lireCfg('/packs', { dispositif_id: dispId }) : Promise.resolve(null)), [dispositif?.id]);
  const aValider = useChargement(() => (dispositif ? lireCfg('/a-valider').catch(() => null) : Promise.resolve(null)), [dispositif?.id]);

  // Lien direct « ?dispositif= » sans organisation (depuis « À valider » ou un écran de dispositif) : l'organisation est lue.
  useEffect(() => {
    if (dispId && !orgId) apiDs.dispositif(dispId).then((d) => { if (d?.organisation_id) changer({ organisation: d.organisation_id }); }).catch(() => {});
  }, [dispId, orgId]);

  const codeGabarit = dispositif ? dispositif.gabarit : null;
  const communes = useMemo(() => toArray(gab.donnees?.communes), [gab.donnees]);
  const reglesGabarit = useMemo(() => [...toArray(toArray(gab.donnees?.gabarits).find((g) => g.code === codeGabarit)?.regles), ...communes], [gab.donnees, codeGabarit, communes]);
  const objet = toArray(objets.donnees).find((o) => o.id === objetId) || null;
  const portee = { dispositifId: dispId, objetType: objetType || null, objetId: objetId || null, libelle: libellePortee({ dispositif, objetType, objet }) };
  const libellePack = (p) => {
    if (!p.portee_objet_id) return 'Dispositif entier';
    const o = toArray(objets.donnees).find((x) => x.id === p.portee_objet_id);
    return `${LIBELLE_OBJET[p.portee_objet_type] || p.portee_objet_type} « ${o ? nomObjet(o) : '…'} »`;
  };
  const regle = reglesGabarit.find((r) => r.code === regleOuverte);
  const rechargerTout = () => { reg.recharger(); packs.recharger(); aValider.recharger(); };

  return (
    <section aria-label="Règlement d'un dispositif" style={{ marginBottom: 16 }}>
      <SelecteurPortee organisations={toArray(orgs.donnees)} orgId={orgId} dispositifs={toArray(disps.donnees)} dispId={dispId} objets={toArray(objets.donnees)}
        valeurObjet={objetType && objetId ? `${objetType}:${objetId}` : ''} date={date} onChanger={changer} onDate={setDate} />
      {orgs.erreur && <BandeauInfo ton="danger">{orgs.erreur}</BandeauInfo>}
      {disps.erreur && <BandeauInfo ton="danger">{disps.erreur}</BandeauInfo>}
      {objets.erreur && <BandeauInfo ton="alerte">{objets.erreur}</BandeauInfo>}
      {gab.erreur && <BandeauInfo ton="danger">{gab.erreur}</BandeauInfo>}
      {!dispositif && <p style={{ fontSize: 14, margin: '0 0 12px' }}>Choisissez une organisation puis un dispositif pour voir et préparer son règlement.</p>}
      {dispositif && (
        <>
          <p style={{ margin: '0 0 12px', fontSize: 14 }} data-portee-choisie="">Portée choisie : <strong>{portee.libelle}</strong></p>
          <CarteAValider donnees={aValider.donnees} dispId={dispId} libellePack={libellePack} onOuvrirPack={(id) => changer({ reglement: id })}
            onOuvrirRegle={(v) => changer({ portee: v.portee_objet_type || '', objet: v.portee_objet_id || '', regle: v.code })} />
          <CarteReglesApplicables reglement={reg.donnees} erreur={reg.erreur} regles={reglesGabarit} communes={communes} objetType={objetType || null} date={date}
            onOuvrir={(code) => changer({ regle: code })} />
          <CarteSimulationEnVigueur key={`${dispId}-${objetId}`} dispositif={dispositif} portee={portee} date={date} />
          <CarteReglements packs={packs.donnees} erreur={packs.erreur} dispositif={dispositif} portee={portee} libellePack={libellePack}
            onOuvrir={(id) => changer({ reglement: id })} onNouveau={() => setFenetre('nouveau')}
            onDemo={(p) => { setErreurDemo(null); packs.recharger(); changer({ reglement: p.id }); }} erreurDemo={{ valeur: erreurDemo, set: setErreurDemo }} />
          {packOuvert && (
            <FicheReglement key={packOuvert} packId={packOuvert} dispositif={dispositif} reglesGabarit={reglesGabarit} libellePack={libellePack} moi={moi}
              onFermer={() => changer({ reglement: '' })} onChange={rechargerTout} />
          )}
        </>
      )}
      {dispositif && regle && (
        <FenetreRegleDispositif key={`${regle.code}-${objetId}`} regle={regle} portee={portee} moi={moi} onFermer={() => changer({ regle: '' })} onChange={rechargerTout} />
      )}
      {dispositif && fenetre === 'nouveau' && (
        <FenetreNouveauReglement dispositif={dispositif} portee={portee} objet={objet} onFermer={() => setFenetre(null)}
          onTermine={(p) => { setFenetre(null); packs.recharger(); changer({ reglement: p.id }); }} />
      )}
    </section>
  );
}

